/* eslint no-inline-comments: 0 */

const Login = () => import( /* webpackChunkName: "group-members-logged-out" */ `./login.vue` );
const OTP = () => import( /* webpackChunkName: "group-members-logged-out" */ `./otp.vue` );
const Forgot = () => import( /* webpackChunkName: "group-members-logged-out" */ `./forgot.vue` );
const DataExport = () => import( /* webpackChunkName: "group-members-logged-out" */ `./data-export.vue` );
const Reset = () => import( /* webpackChunkName: "group-members-logged-out" */ `./reset.vue` );
const Register = () => import( /* webpackChunkName: "group-members-logged-out" */ `./register.vue` );
const EmailPreferencesLoggedOut = () => import( /* webpackChunkName: "group-members-logged-out" */ `./email-preferences/logged-out.vue` );
const StartPlan = () => import( /* webpackChunkName: "group-members-logged-out" */ `./start-plan.vue` );
const SubscriptionConfirmed = () => import( /* webpackChunkName: "group-members-logged-out" */ `./subscription-confirmed.vue` );

const Consent = () => import( /* webpackChunkName: "group-members-logged-in" */ `./consent.vue` );
const ConditioningConsent = () => import( /* webpackChunkName: "group-members-logged-in" */ `./conditioning-consent.vue` );
const Request = () => import( /* webpackChunkName: "group-members-logged-in" */ `./subscriptions/request.vue` );
const Authorize = () => import( /* webpackChunkName: "group-members-logged-in" */ `./authorize.vue` );
const Profile = () => import( /* webpackChunkName: "group-members-logged-in" */ `./profile.vue` );
const EmailPreferencesLoggedIn = () => import( /* webpackChunkName: "group-members-logged-in" */ `./email-preferences/logged-in.vue` );
const Security = () => import( /* webpackChunkName: "group-members-logged-in" */ `./security.vue` );
const PasskeyUpgrade = () => import( /* webpackChunkName: "group-members-logged-in" */ `./passkey-upgrade.vue` );

const ManageSubscription = () => import( /* webpackChunkName: "group-members-logged-in" */ `./subscriptions/manage.vue` );
const MyAgency = () => import( /* webpackChunkName: "group-members-logged-in" */ `./my-agency.vue` );
const RequestOrgAccess = () => import( /* webpackChunkName: "group-members-logged-in" */ `./organizations/request-access.vue` );
const JoinAgency = () => import( /* webpackChunkName: "group-members-logged-in" */ `./join-agency.vue` );
const AccountHome = () => import( /* webpackChunkName: "group-members-logged-in" */ `./account-home.vue` );
const AccountEmailPreferences = () => import( /* webpackChunkName: "group-members-logged-in" */ `./account-email-preferences.vue` );
const PortalHome = () => import( /* webpackChunkName: "group-members-logged-in" */ `./portal-home.vue` );
const ConnectedTools = () => import( /* webpackChunkName: "group-members-logged-in" */ `./connected-tools.vue` );

export const members_logged_out_routes = [
	{
		path: ``,
		children: [
			{
				path: ``,
				name: `dashboard`,
				component: Login,
				meta: {
					title: `Log In`
				}
			},
			{
				path: `otp`,
				name: `otp`,
				component: OTP,
				meta: {
					title: `Two-Factor Authentication`
				}
			},
			{
				path: `forgot`,
				name: `forgot`,
				component: Forgot,
				meta: {
					title: `Forgot Password`
				}
			},
			{
				path: `data-export`,
				name: `data-export`,
				component: DataExport,
				meta: {
					title: `Download Your Data`
				}
			},
			{
				path: `reset`,
				name: `reset`,
				component: Reset,
				meta: {
					title: `Reset Password`
				}
			},
			{
				path: `register`,
				name: `register`,
				component: Register,
				meta: {
					title: `Register`
				}
			},
			{
				// The plan path. Registered in BOTH route tables with the same
				// path and the same component: signed out it authenticates,
				// signed in it hands off. `authenticated` is a static prop rather
				// than a store read because these two tables are already the
				// source of truth for that distinction.
				//
				// Leaving it out of the logged-in table would not 404, which is
				// worse: the portal's catchAll redirects to the dashboard with
				// `query: {}`, so the plan would be silently dropped at the exact
				// moment the emailed link succeeded.
				path: `plan`,
				name: `plan`,
				component: StartPlan,
				props: {
					authenticated: false
				},
				meta: {
					title: `Start Your Plan`
				}
			},
			{
				path: `email-preferences`,
				name: `email-preferences`,
				component: EmailPreferencesLoggedOut,
				meta: {
					title: `Email Preferences`
				}
			},
			{
				// The double opt-in landing. Logged out: the visitor arrives
				// from a link in their mail with no session, exactly like
				// email-preferences above.
				//
				// email-preferences stays registered and keeps confirming a
				// double opt-in token, because every confirmation email sent
				// before this route existed points at it and those live in
				// mailboxes indefinitely.
				path: `subscription-confirmed`,
				name: `subscription-confirmed`,
				component: SubscriptionConfirmed,
				meta: {
					title: `Subscription Confirmed`
				}
			}
		]
	}
];

// #2465: a brand portal page that moved to the accounts host. The server
// already redirects a page load of the old URL (members_portal/
// account_page_redirects.go); this covers a navigation inside the SPA to the
// old route name (the MFA gate's `security`, say), so nothing lands on a page
// the portal no longer has. The query string is kept, as the server keeps it.
function leave_for( base_url, path ) {
	return to => {
		const target = new URL( path, base_url );

		target.search = new URLSearchParams( to.query ).toString();

		location.assign( target.toString() );

		return false;
	};
}

// The logged-in pages of a brand portal whose profile, security and email
// preferences are on the accounts host (response.accounts_url, set by the
// same rule the server redirects by). The route names stay, so every existing
// link to them keeps resolving, and each one leaves for its new home.
function move_account_pages( children, response ) {
	const accounts_url = response.accounts_url;
	const moved = [];

	for( const child of children ) {
		switch( child.name ) {
		case `dashboard`:
			moved.push( {
				...child,
				component: PortalHome,
				meta: {
					title: `Your Account`
				}
			} );
			break;
		case `profile`:
		case `security`:
			moved.push( {
				path: child.path,
				name: child.name,
				beforeEnter: leave_for( accounts_url, `/${child.path}` ),
				component: PortalHome
			} );
			break;
		case `email-preferences`:
			// An emailed link for THIS member (a newsletter footer, a double
			// opt-in confirmation) is still handled here, signed in or not,
			// by the page that handles it signed out; every other visit
			// goes to the account's own page. A link for somebody else goes
			// there too, rather than editing the signed-in member's lists
			// from another person's page.
			moved.push( {
				...child,
				component: EmailPreferencesLoggedOut,
				beforeEnter: to => {
					if( to.query.member_uuid && to.query.member_uuid === response.member?.uuid ) {
						return true;
					}

					return leave_for( accounts_url, `/email-preferences` )( {
						query: {}
					} );
				}
			} );
			break;
		case `my-agency`:
		case `join-agency`:
			// Agencies are managed in the app now.
			moved.push( {
				path: child.path,
				name: child.name,
				beforeEnter: response.app_url
					? leave_for( response.app_url, `/` )
					: () => ( {
						name: `dashboard`
					} ),
				component: PortalHome
			} );
			break;
		default:
			moved.push( child );
		}
	}

	moved.push( {
		path: `connected-tools`,
		name: `connected-tools`,
		component: ConnectedTools,
		meta: {
			title: `Connected Tools`
		}
	} );

	return moved;
}

export function get_members_logged_in_routes( response = {} ) {
	const routes = [];

	routes.push(
		{
			path: ``,
			children: [
				{
					// Was the subscriptions/orgs dashboard (dashboard.vue, removed --
					// my.di.ai is now account-management only; a subscription's
					// product home lives on app.di.ai). Profile is the new default
					// landing page for a logged-in member, and `dashboard` stays the
					// route NAME so every existing `{ name: 'dashboard' }` link --
					// the site logo, the back-links on half the logged-in pages --
					// keeps resolving without being rewritten one by one.
					path: ``,
					name: `dashboard`,
					component: Profile,
					meta: {
						title: `Profile`
					}
				},
				{
					path: `otp`,
					redirect: {
						name: `dashboard`
					}
				},
				{
					// See the logged-out entry: same path, same component, and
					// this half is the one the emailed magic link lands on.
					path: `plan`,
					name: `plan`,
					component: StartPlan,
					props: {
						authenticated: true
					},
					meta: {
						title: `Start Your Plan`
					}
				},
				{
					path: `consent`,
					name: `consent`,
					component: Consent,
					meta: {
						title: `Consent`
					}
				},
				{
					path: `conditioning-consent`,
					name: `conditioning-consent`,
					component: ConditioningConsent,
					meta: {
						title: `Consent`
					}
				},
				{
					path: `request`,
					name: `request`,
					component: Request,
					meta: {
						// "Contact Us" (founder wording pass, 2026-09-09:
						// more general than "Contact Sales" -- this form
						// isn't sales-specific).
						title: `Contact Us`
					}
				},
				{
					path: `oauth2/authorize`,
					name: `authorize`,
					component: Authorize,
					meta: {
						title: `Authorize`
					}
				},
				{
					path: `profile`,
					name: `profile`,
					component: Profile,
					meta: {
						title: `Profile`
					}
				},
				{
					path: `email-preferences`,
					name: `email-preferences`,
					component: EmailPreferencesLoggedIn,
					meta: {
						title: `Email Preferences`
					}
				},
				{
					path: `security`,
					name: `security`,
					component: Security,
					meta: {
						title: `Security`
					}
				},
				{
					// #2359: offered after a sign-in that did not use a
					// passkey (utilities' land_after_login). Logged-in only:
					// the sign-in has already made the session it lands on.
					path: `passkey-upgrade`,
					name: `passkey-upgrade`,
					component: PasskeyUpgrade,
					meta: {
						title: `Add a Passkey`
					}
				},
				{
					// The data-subject export. Registered in BOTH route tables
					// with the same path and the same component, the shape the
					// `plan` route above already uses.
					//
					// It was reachable only signed OUT, from a link under the
					// password field beside "Forgot Password", where it read as
					// a third way to log in; and from none of the signed-in
					// pages at all, so a member who HAD an account could not
					// find the one page that hands them their data. The Security
					// page links here.
					//
					// The signed-out entry point stays, and stays
					// unauthenticated, which is the load-bearing half: the
					// handler it posts to serves newsletter subscribers and
					// pixel-tracked visitors who never had a login, and putting
					// it behind a session would remove the right from exactly
					// the people who cannot exercise it any other way. See
					// members_portal/dsar_export_api.go.
					path: `data-export`,
					name: `data-export`,
					component: DataExport,
					meta: {
						title: `Download Your Data`
					}
				},
				{
					path: `my-agency`,
					name: `my-agency`,
					component: MyAgency,
					meta: {
						title: `Manage Agency`
					}
				},
				{
					path: `join-agency`,
					name: `join-agency`,
					component: JoinAgency,
					meta: {
						title: `Join an Agency`
					}
				},
				{
					// Step one of the two-step model: ask to be put in touch
					// with whoever manages an already-claimed organization's
					// profile, before requesting subscription access to it
					// (the `request` route above). See
					// members_portal/profile_inquiry_api.go.
					path: `organizations/request-access`,
					name: `request-org-access`,
					component: RequestOrgAccess,
					meta: {
						// Browser-tab title kept in sync with the sidebar's
						// nav label (founder wording pass, 2026-09-09) --
						// see assets/js/container.vue's comment on this row
						// for what did and did not change.
						title: `Request Subscription Access`
					}
				},
				{
					// view-subscription (read-only subscription detail) is removed
					// entirely: my.di.ai is account-management only now, and that
					// surface belongs on app.di.ai. manage-subscription stays, but
					// trimmed to billing/cancellation/identifying info -- the
					// verticals/organization-filters/tags/articles children that
					// used to hang off it were org-wide subscription content
					// configuration, reachable ONLY from the manage page's own
					// links, and are removed along with those links.
					path: `subscriptions`,
					children: [
						{
							path: `manage/:uuid`,
							name: `manage-subscription`,
							component: ManageSubscription,
							meta: {
								title: `Manage Subscription`
							}
						}
					]
				}
			]
		}
	);

	if( response.accounts_url ) {
		routes[0].children = move_account_pages( routes[0].children, response );
	}

	return routes;
}

// The central accounts host (#2358): account.wiretaplabs.com. The same members
// echo instance serves it, so every members API answers there, but its SPA is
// deliberately a small page set: sign-in, register, forgot/reset, the second
// factor, OAuth2 authorize and consent, a signed-in home listing the apps,
// and the account's own settings, which are one person's across every app
// (#2465): profile, security and privacy, and email preferences. What belongs
// to one brand (subscriptions and billing, requesting access) stays on the
// brand portals; the catch-all sends any other path to the home.
//
// The page components are the members portal's own; each shows the app-branded
// heading (_sign-in-heading.vue) only when it is on this host.
const accounts_logged_out_paths = [``, `otp`, `forgot`, `reset`, `register`];

export const accounts_logged_out_routes = [
	{
		path: ``,
		children: members_logged_out_routes[0].children.filter( r => accounts_logged_out_paths.includes( r.path ) )
	}
];

export function get_accounts_logged_in_routes() {
	return [
		{
			path: ``,
			children: [
				{
					path: ``,
					name: `dashboard`,
					component: AccountHome,
					meta: {
						title: `Your Account`
					}
				},
				{
					path: `otp`,
					redirect: {
						name: `dashboard`
					}
				},
				{
					path: `consent`,
					name: `consent`,
					component: Consent,
					meta: {
						title: `Consent`
					}
				},
				{
					path: `oauth2/authorize`,
					name: `authorize`,
					component: Authorize,
					meta: {
						title: `Authorize`
					}
				},
				{
					// #2465: the member profile lives here, once, for every
					// app. It is the same member row every brand portal
					// edited, so there is one place to change it.
					path: `profile`,
					name: `profile`,
					component: Profile,
					meta: {
						title: `Profile`
					}
				},
				{
					path: `security`,
					name: `security`,
					component: Security,
					meta: {
						title: `Security and Privacy`
					}
				},
				{
					// #2465: every app's email preferences, per person, in one
					// place. The brand portals' signed-in pages redirect here;
					// their tokenized, signed-out page stays where it is.
					path: `email-preferences`,
					name: `email-preferences`,
					component: AccountEmailPreferences,
					meta: {
						title: `Email Preferences`
					}
				},
				{
					// security.vue links here, and it is the signed-in half of
					// a privacy right (see the members table's comment).
					path: `data-export`,
					name: `data-export`,
					component: DataExport,
					meta: {
						title: `Download Your Data`
					}
				}
			]
		}
	];
}
